import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { api, ApiError } from '@/lib/api'
import { facebookLogin as fbLogin } from '@/lib/facebook'
import { googleLogin as gLogin } from '@/lib/google'

interface User {
  _id: string
  email: string
  fullName: string
  role: string
  isPremium: boolean
  avatar?: string
  bio?: string
  phone?: string
  followers: string[]
  following: string[]
  teacherId?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithFacebook: () => Promise<void>
  loginWithGoogle: (mode?: 'login' | 'register') => Promise<{ requireOtp: boolean; email?: string }>
  register: (email: string, password: string, fullName: string) => Promise<void>
  verifyOtp: (email: string, code: string) => Promise<void>
  logout: () => void
  setUser: (user: User | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem('user')
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, _setUser] = useState<User | null>(readCachedUser)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)
  const didBootstrapRef = useRef(false)

  const setUser = useCallback((newUser: User | null) => {
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser))
    } else {
      localStorage.removeItem('user')
    }
    _setUser(newUser)
  }, [])

  useEffect(() => {
    if (didBootstrapRef.current) return
    didBootstrapRef.current = true

    const stored = localStorage.getItem('token')
    if (stored) {
      api.users.getMe({ silent: true })
        .then(setUser)
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 401) {
            localStorage.removeItem('token')
            setToken(null)
            setUser(null)
          }
        })
        .finally(() => setLoading(false))
    } else {
      setUser(null)
      setLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const res = await api.auth.login({ email, password })
    localStorage.setItem('token', res.token)
    setToken(res.token)
    setUser(res.user)
  }

  const loginWithFacebook = async () => {
    const accessToken = await fbLogin()
    const res = await api.auth.facebook(accessToken)
    localStorage.setItem('token', res.token)
    setToken(res.token)
    setUser(res.user)
  }

  const loginWithGoogle = async (mode: 'login' | 'register' = 'login'): Promise<{ requireOtp: boolean; email?: string }> => {
    const credential = await gLogin()
    const res = await api.auth.google(credential, mode)
    if ('requireOtp' in res && res.requireOtp) {
      return { requireOtp: true, email: res.email }
    }
    const r = res as { token: string; user: any }
    localStorage.setItem('token', r.token)
    setToken(r.token)
    setUser(r.user)
    return { requireOtp: false }
  }

  const register = async (email: string, password: string, fullName: string) => {
    await api.auth.register({ email, password, fullName })
  }

  const verifyOtp = async (email: string, code: string) => {
    const res = await api.auth.verifyOtp({ email, code })
    localStorage.setItem('token', res.token)
    setToken(res.token)
    setUser(res.user)
  }

  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithFacebook, loginWithGoogle, register, verifyOtp, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
